
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUpRight, ArrowDownRight, MoreHorizontal, X, TrendingUp, Shield, Zap, History } from 'lucide-react';
import { Position } from '../types/portfolio';
import { logInteraction } from '../lib/utils';

interface PositionsTableProps {
  positions: Position[];
  onMoreOptions?: (pos: Position) => void;
}

export function PositionsTable({ positions, onMoreOptions }: PositionsTableProps) {
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: keyof Position; direction: 'asc' | 'desc' } | null>(null);

  const sortedPositions = [...positions].sort((a, b) => {
    if (!sortConfig) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }
    
    const aNum = parseFloat(String(aValue));
    const bNum = parseFloat(String(bValue));
    
    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
  });

  const handleSort = (key: keyof Position) => {
    logInteraction('PositionsTable', 'Sort', { key });
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRowClick = (pos: Position) => {
    logInteraction('PositionsTable', 'Select Position', { symbol: pos.symbol });
    setSelectedPosition(pos);
  };

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
      <div className="p-4 border-b border-[var(--card-border)] bg-[var(--card-header-bg)] flex items-center justify-between">
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-tight italic">Current Positions</h3>
        <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest bg-[var(--input-bg)] px-2 py-1 rounded border border-[var(--input-border)]">
          {positions.length} Assets
        </span>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[var(--card-border)] bg-[var(--card-header-bg)]/50">
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest cursor-pointer hover:text-[var(--app-text)]" onClick={() => handleSort('symbol')}>Asset</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Qty</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Avg Price</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Market Price</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right cursor-pointer hover:text-[var(--app-text)]" onClick={() => handleSort('unrealized_pl')}>Unrealized P&L</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-center">AI Rating</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedPositions.map((pos) => {
              const pl = parseFloat(pos.unrealized_pl);
              const plpc = parseFloat(pos.unrealized_plpc) * 100;
              const isPositive = pl >= 0;

              return (
                <motion.tr 
                  key={pos.symbol}
                  layoutId={pos.symbol}
                  onClick={() => handleRowClick(pos)}
                  className="border-b border-[var(--card-border)] last:border-0 hover:bg-[var(--input-bg)]/50 cursor-pointer transition-colors group"
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center font-black text-xs text-slate-600">
                        {pos.symbol[0]}
                      </div>
                      <div>
                        <div className="font-black text-[var(--app-text)] text-sm tracking-tight">{pos.symbol}</div>
                        <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{pos.exchange}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right font-mono text-xs font-bold text-[var(--app-text)]">{pos.qty}</td>
                  <td className="p-4 text-right font-mono text-xs font-bold text-[var(--app-text)]">${parseFloat(pos.avg_entry_price).toFixed(2)}</td>
                  <td className="p-4 text-right font-mono text-xs font-bold text-[var(--app-text)]">${parseFloat(pos.current_price).toFixed(2)}</td>
                  <td className="p-4 text-right">
                    <div className={`font-mono text-xs font-black ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isPositive ? '+' : ''}${pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className={`text-[10px] font-bold italic ${isPositive ? 'text-emerald-600/70' : 'text-rose-600/70'}`}>
                      {isPositive ? '+' : ''}{plpc.toFixed(2)}%
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className={`px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-tighter italic ${
                      pos.ai_rating?.includes('Buy') ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' :
                      pos.ai_rating?.includes('Sell') ? 'bg-rose-500/10 text-rose-600 border border-rose-500/20' :
                      'bg-slate-500/10 text-slate-600 border border-slate-500/20'
                    }`}>
                      {pos.ai_rating}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        logInteraction('PositionsTable', 'More Options', { symbol: pos.symbol });
                        onMoreOptions?.(pos);
                      }}
                      className="p-1.5 text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--card-border)] rounded-md transition-all active:scale-95"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selectedPosition && (
          <PositionDrawer 
            position={selectedPosition} 
            onClose={() => setSelectedPosition(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PositionDrawer({ position, onClose }: { position: Position; onClose: () => void }) {
  const pl = parseFloat(position.unrealized_pl);
  const isPositive = pl >= 0;

  return (
    <>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
      />
      <motion.div 
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 h-full w-full max-w-md bg-[var(--card-bg)] shadow-2xl z-50 border-l border-[var(--card-border)] overflow-y-auto"
      >
        <div className="p-6 border-b border-[var(--card-border)] flex items-center justify-between sticky top-0 bg-[var(--card-bg)] z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center font-black text-xl text-slate-600">
              {position.symbol[0]}
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--app-text)] tracking-tight italic">{position.symbol}</h2>
              <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">{position.exchange} • {position.asset_class.replace('_', ' ')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--input-bg)] rounded-full transition-colors">
            <X size={20} className="text-[var(--muted-text)]" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Main Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-[var(--input-bg)] rounded-2xl border border-[var(--input-border)]">
              <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block mb-1">Market Value</span>
              <span className="text-xl font-black text-[var(--app-text)] font-mono">${parseFloat(position.market_value).toLocaleString()}</span>
            </div>
            <div className="p-4 bg-[var(--input-bg)] rounded-2xl border border-[var(--input-border)]">
              <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block mb-1">Unrealized P&L</span>
              <div className={`flex items-center gap-1 font-black font-mono text-xl ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                {isPositive ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}
                ${Math.abs(pl).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-[0.2em] border-b border-[var(--card-border)] pb-2">Position Details</h4>
            <div className="grid grid-cols-2 gap-y-4">
              <div>
                <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block">Quantity</span>
                <span className="text-sm font-black text-[var(--app-text)] font-mono">{position.qty}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block">Average Price</span>
                <span className="text-sm font-black text-[var(--app-text)] font-mono">${parseFloat(position.avg_entry_price).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block">Current Price</span>
                <span className="text-sm font-black text-[var(--app-text)] font-mono">${parseFloat(position.current_price).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest block">Cost Basis</span>
                <span className="text-sm font-black text-[var(--app-text)] font-mono">${parseFloat(position.cost_basis).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-[0.2em] border-b border-[var(--card-border)] pb-2">AI Position Analysis</h4>
            <div className="p-5 bg-slate-900 rounded-2xl border border-slate-800 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Zap size={80} className="text-emerald-400" />
              </div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} className="text-emerald-400" />
                    <span className="text-xs font-black text-white uppercase italic tracking-tight">Adversyn AI Rating</span>
                  </div>
                  <span className="text-xs font-black text-emerald-400 uppercase italic tracking-tight">{position.ai_rating}</span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Confidence Score</span>
                    <span className="text-xs font-black text-white">{position.ai_confidence}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${position.ai_confidence}%` }}
                      className="h-full bg-emerald-500"
                    />
                  </div>
                  <p className="text-[10px] font-medium text-slate-400 leading-relaxed italic">
                    Trend remains bullish on the 4H timeframe. Institutional accumulation detected at current levels. Recommendation: <span className="text-white font-bold">HOLD</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Exposure */}
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-[0.2em] border-b border-[var(--card-border)] pb-2">Risk Exposure</h4>
            <div className="flex items-center justify-between p-4 bg-[var(--input-bg)] rounded-xl border border-[var(--input-border)]">
              <div className="flex items-center gap-3">
                <Shield size={18} className="text-amber-500" />
                <span className="text-xs font-bold text-[var(--app-text)] uppercase tracking-tight">Risk Level</span>
              </div>
              <span className="text-xs font-black text-amber-600 uppercase italic tracking-tight">Medium</span>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-[0.2em] border-b border-[var(--card-border)] pb-2">Trade History</h4>
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--card-border)] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-emerald-500/10 rounded-md">
                      <History size={14} className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-xs font-black text-[var(--app-text)]">BUY {position.symbol}</div>
                      <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Oct 12, 2023 • 10:45 AM</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-black text-[var(--app-text)] font-mono">125 Qty</div>
                    <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest font-mono">$112.40</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}
